import { reactive } from 'vue';
import { defineStore, acceptHMRUpdate } from 'pinia';
import i18n from '@/js/plugins/i18n';
import useUiMsgboxStore from '@/stores/ui/msgbox';
import useUiPlanningStore from '@/stores/ui/planning';
import Planning from '@/models/Planning';
import PlanningElement from '@/models/PlanningElement';
import useSubprojectsStore from '@/stores/subprojects';

const invariantFields = ['id'];

const store = defineStore('ui-subplanning', {
  state: () => ({}),

  getters: {
    getOriginalPlanningEl() {
      return (el) => {
        const originalPlanningEl = new PlanningElement(el.getPlanning(), { ...el.getAll(), id: el.o_id });
        originalPlanningEl.dashboard_el = el;
        return originalPlanningEl;
      };
    },
    getOriginalLaneId() {
      return el => el.getPlanning()?.lanes.find(lane => lane.id == el.getLaneId())?.o_id;
    },
  },

  actions: {
    saveEl({ el, oldState: oldStateParam, newState: newStateParam, props = [] }) {
      // el is dashboard_el
      // use oldState if el has been modified, or newState otherwise
      let oldState = oldStateParam;
      let newState = newStateParam;
      if (newState && ! oldState) {
        // apply new state
        newState = _.omit(newState, invariantFields);
        oldState = _.omit(el.getAll(), invariantFields);
        el.set(newState); // set rather than reset does not modify el if not needed (prevents reactive workloads/other_computed update)
        el.update();
      }
      newState = _.omit(el.getAll(), invariantFields);

      let savingProps = props.length ? props : Object.keys(newState);
      savingProps = savingProps.filter(prop => ! angular.equals(newState[prop], oldState[prop]) && ! invariantFields.includes(prop));
      if (! savingProps.length) return Promise.resolve('nothing to save');

      const { lastSavedProjectVersion } = el.getPlanning();
      const originalPlanning = new Planning({
        id: lastSavedProjectVersion.id,
        meta: { title: lastSavedProjectVersion.title },
        ...lastSavedProjectVersion.content,
      });

      const originalPlanningEl = originalPlanning.elements.find(item => item.id == el.o_id);
      if (! originalPlanningEl) return Promise.resolve('nothing to save');
      originalPlanningEl.set(_.pick(newState, savingProps));
      if (savingProps.includes('lane_id')) {
        const originalLaneId = this.getOriginalLaneId(el);
        if (originalLaneId) originalPlanningEl.setLaneId(originalLaneId);
      }
      return originalPlanning.save().then((contentPatch) => {
        if (contentPatch) window.notificationsSrv.callEvent('projectSaved', { contentPatch, planning_id: originalPlanning.id, planning_title: originalPlanning.getTitle() });
      }).catch((message) => {
        el.set(oldState);
        el.update();
        if (message) useUiMsgboxStore().open({ title: i18n.global.t('MONITORING_PROGRESS.ERROR_NOT_MODIFIED'), body: message || "" });
        return 'nothing to save';
      });
    },
    deleteElement(el) {
      // el is dashboard_el
      // remove el
      const subplanning = Object.values(useSubprojectsStore().byLaneId).find(item => item.id == el.getPlanning().id); // get reactive subplanning from store
      if (! subplanning) return;
      const index = subplanning.elements.findIndex(element => element.id == el.id);
      if (index > -1) subplanning.elements.splice(index, 1);

      // save
      const { lastSavedProjectVersion } = el.getPlanning();
      const originalPlanning = new Planning({ id: lastSavedProjectVersion.id, meta: { title: lastSavedProjectVersion.title }, ...lastSavedProjectVersion.content });
      const originalPlanningElIndex = originalPlanning.elements.findIndex(item => item.id == el.o_id);
      if (originalPlanningElIndex == -1) return;
      originalPlanning.elements.splice(originalPlanningElIndex, 1);
      originalPlanning.save().then((contentPatch) => {
        if (contentPatch) window.notificationsSrv.callEvent('projectSaved', { contentPatch, planning_id: subplanning.id, planning_title: subplanning.getTitle() });
      }).catch((message) => {
        subplanning.elements.push(el);
        el.update();
        if (message) useUiMsgboxStore().open({ title: i18n.global.t('MONITORING_PROGRESS.ERROR_NOT_MODIFIED'), body: message || "" });
      });
    },
    duplicateElement(el) {
      // el is dashboard_el
      const newEl = reactive(this.getOriginalPlanningEl(el));
      if (! newEl) return;
      const originalLaneId = this.getOriginalLaneId(newEl);
      if (originalLaneId) newEl.setLaneId(originalLaneId);
      newEl.setDependencies(null);
      newEl.create().then(() => { // create a new element via api / id is updated by api response
        const subplanning = el.getPlanning();
        if (! subplanning) return;
        subplanning.lastSavedProjectVersion.content.elements.push(newEl.getAll()); // update lastSavedProjectVersion
        useSubprojectsStore().addSubplanningElement({ projectId: subplanning.id, el: newEl });
        newEl.update();
        setTimeout(() => {
          newEl.updateHeight();
        });
        window.notificationsSrv.callEvent('projectSaved', { planning_id: subplanning.id, planning_title: subplanning.getTitle() });
      }).catch((message) => {
        if (message) useUiMsgboxStore().open({ title: i18n.global.t('MONITORING_PROGRESS.ERROR_NOT_MODIFIED'), body: message || "" });
      });
    },
    closeElementDetails(reason) {
      const { openedElement } = useUiPlanningStore();
      if (! openedElement) return;
      if (reason == 'delete') {
        this.deleteElement(openedElement.dashboard_el);
      } else {
        this.saveEl({ el: openedElement.dashboard_el, newState: openedElement.getAll() });
        if (reason == 'duplicate') {
          this.duplicateElement(openedElement.dashboard_el);
        }
      }
      useUiPlanningStore().openElement(null);
    },
    closeColorPicker() {
      const { openedColorPickerElement } = useUiPlanningStore();
      if (! openedColorPickerElement) return;
      this.saveEl({ el: openedColorPickerElement.dashboard_el, newState: openedColorPickerElement.getAll() });

      useUiPlanningStore().openColorPickerElement(null);
    },
  },
});

if (import.meta.hot) import.meta.hot.accept(acceptHMRUpdate(store, import.meta.hot));
export default store;
